import { Box, Paper, Stack, Typography } from "@mui/material";

export const pageContainerSx = {
	width: "100%",
	minHeight: "calc(100vh - 64px)",
	px: { xs: 2, sm: 2.5, md: 3 },
	py: { xs: 1.5, sm: 2 },
	boxSizing: "border-box",
	bgcolor: "#f7f8fa",
};

export const explorePageSx = {
	width: "100%",
	minHeight: "calc(100vh - 60px)",
	px: { xs: 2, sm: 3, md: 4 },
	py: { xs: 2.5, sm: 3 },
	boxSizing: "border-box",
	bgcolor: "#f7f8fa",
};

export const panelSx = {
	borderRadius: 2,
	border: "1px solid #e6e8ec",
	bgcolor: "#fff",
	boxShadow: "none",
	boxSizing: "border-box",
	maxWidth: "100%",
};

export const primaryButtonSx = {
	textTransform: "none",
	fontWeight: 600,
	bgcolor: "#1f2937",
	borderRadius: 1.5,
	px: 2,
	"&:hover": { bgcolor: "#111827" },
};

export const outlinedButtonSx = {
	textTransform: "none",
	fontWeight: 600,
	borderRadius: 1.5,
	borderColor: "#d1d5db",
	color: "#374151",
	"&:hover": { borderColor: "#9ca3af", bgcolor: "#f9fafb" },
};

export const compactInputSx = {
	"& .MuiOutlinedInput-root": {
		borderRadius: 1.5,
		bgcolor: "#fff",
		fontSize: "0.8125rem",
		"& fieldset": { borderColor: "#e5e7eb" },
		"&:hover fieldset": { borderColor: "#d1d5db" },
		"&.Mui-focused fieldset": { borderColor: "#6b7280", borderWidth: 1 },
	},
	"& .MuiInputLabel-root": { fontSize: "0.8125rem" },
};

export const compactDateSlotProps = {
	textField: {
		size: "small",
		sx: {
			width: { xs: "100%", sm: 140 },
			flexShrink: 0,
			...compactInputSx,
		},
	},
};

export function PageHeader({ title, description, icon, action }) {
	return (
		<Stack
			direction={{ xs: "column", sm: "row" }}
			alignItems={{ xs: "flex-start", sm: "center" }}
			justifyContent="space-between"
			spacing={1.5}
			mb={2}
		>
			<Stack direction="row" spacing={1.25} alignItems="center" minWidth={0}>
				{icon ? (
					<Box
						sx={{
							width: 36,
							height: 36,
							borderRadius: 1.5,
							display: "grid",
							placeItems: "center",
							bgcolor: "#fff",
							border: "1px solid #e6e8ec",
							color: "#1f2937",
							flexShrink: 0,
							"& .MuiSvgIcon-root": { fontSize: 20 },
						}}
					>
						{icon}
					</Box>
				) : null}
				<Box minWidth={0}>
					<Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25, color: "#111827" }}>
						{title}
					</Typography>
					{description ? (
						<Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
							{description}
						</Typography>
					) : null}
				</Box>
			</Stack>
			{action}
		</Stack>
	);
}

export function Panel({ children, sx, ...props }) {
	return (
		<Paper elevation={0} sx={{ ...panelSx, ...sx }} {...props}>
			{children}
		</Paper>
	);
}

export function KpiStat({ label, value, tone = "neutral" }) {
	const tones = {
		neutral: { value: "#111827", chip: "#f3f4f6" },
		success: { value: "#15803d", chip: "#f0fdf4" },
		danger: { value: "#b91c1c", chip: "#fef2f2" },
		warn: { value: "#b45309", chip: "#fffbeb" },
	};
	const t = tones[tone] || tones.neutral;

	return (
		<Box
			sx={{
				flex: "1 1 0",
				minWidth: 0,
				px: 1.25,
				py: 1,
				borderRight: "1px solid #eef0f3",
				"&:last-child": { borderRight: "none" },
			}}
		>
			<Typography
				sx={{
					fontSize: "0.68rem",
					fontWeight: 600,
					letterSpacing: 0.4,
					textTransform: "uppercase",
					color: "#6b7280",
					mb: 0.35,
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{label}
			</Typography>
			<Typography
				sx={{
					fontSize: "1.15rem",
					fontWeight: 700,
					color: t.value,
					lineHeight: 1.2,
					fontVariantNumeric: "tabular-nums",
					whiteSpace: "nowrap",
					overflow: "hidden",
					textOverflow: "ellipsis",
				}}
			>
				{value}
			</Typography>
		</Box>
	);
}
